// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes for each scene
export const SCENE_COLORS = {
  scene1: { primaryColor: '#6B7280', accentColor: '#EF4444' },
  scene2: { primaryColor: '#22C55E', accentColor: '#ffffff' },
  scene3: { primaryColor: '#ffffff', accentColor: '#F59E0B' },
  scene4: { primaryColor: '#F59E0B', accentColor: '#ffffff' },
  scene5: { primaryColor: '#EF4444', accentColor: '#ffffff' },
  scene6: { primaryColor: '#3B82F6', accentColor: '#FCD34D' },
  scene7: { primaryColor: '#FCD34D', accentColor: '#ffffff' },
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },
};

// Background images for each scene
export const SCENE_IMAGES = {
  scene1: 'images/gdm-quiet-phone/scene1-quiet-phone.png',
  scene2: 'images/gdm-quiet-phone/scene2-ringing-phone.png',
  scene3: 'images/gdm-quiet-phone/scene3-neighborhood.png',
  scene4: 'images/gdm-quiet-phone/scene4-technician.png',
  scene5: 'images/gdm-quiet-phone/scene5-search-results.png',
  scene6: 'images/gdm-quiet-phone/scene6-analytics.png',
  scene7: 'images/gdm-quiet-phone/scene7-report.png',
};

// Hg market report presentation slides (captured from reports.garagedoormarketers.com)
export const REPORT_SLIDES = [
  'images/gdm-quiet-phone/report-slide-01.png',
  'images/gdm-quiet-phone/report-slide-05.png',
  'images/gdm-quiet-phone/report-slide-07.png',
  'images/gdm-quiet-phone/report-slide-12.png',
];

// Word timings extracted from audio using Whisper (incredibly-fast-whisper)
// Voice: Mark (UgBBYS2sOqTuMpoF3BR0) - American male
// Manual corrections: punctuation + same-day merge
export const WORD_TIMINGS: WordTiming[] = [
  { text: 'Your', startMs: 0, endMs: 120 },
  { text: 'phone.', startMs: 120, endMs: 560 },
  { text: 'Quiet.', startMs: 560, endMs: 1520 },
  { text: 'Your', startMs: 1520, endMs: 2220 },
  { text: 'competitor\'s', startMs: 2220, endMs: 2720 },
  { text: 'phone.', startMs: 2720, endMs: 3060 },
  { text: 'Ringing.', startMs: 3060, endMs: 3540 },
  { text: 'Same', startMs: 3540, endMs: 4380 },
  { text: 'city.', startMs: 4380, endMs: 4760 },
  { text: 'Same', startMs: 4760, endMs: 5060 },
  { text: 'services.', startMs: 5060, endMs: 5820 },
  { text: 'Different', startMs: 5820, endMs: 7000 },
  { text: 'results.', startMs: 7000, endMs: 7620 },
  { text: 'They\'re', startMs: 7620, endMs: 8200 },
  { text: 'getting', startMs: 8200, endMs: 8400 },
  { text: 'the', startMs: 8400, endMs: 8540 },
  { text: 'emergency', startMs: 8540, endMs: 8860 },
  { text: 'repairs.', startMs: 8860, endMs: 9580 },
  { text: 'The', startMs: 10180, endMs: 10260 },
  { text: 'commercial', startMs: 10260, endMs: 10560 },
  { text: 'installs.', startMs: 10560, endMs: 11320 },
  { text: 'The', startMs: 11320, endMs: 11780 },
  { text: 'same-day', startMs: 11780, endMs: 12140 },
  { text: 'jobs.', startMs: 12140, endMs: 13100 },
  { text: 'Why?', startMs: 13100, endMs: 14060 },
  { text: 'They', startMs: 14760, endMs: 14940 },
  { text: 'own', startMs: 14940, endMs: 15800 },
  { text: 'the', startMs: 15800, endMs: 15920 },
  { text: 'search', startMs: 15920, endMs: 16100 },
  { text: 'terms', startMs: 16100, endMs: 16380 },
  { text: 'you', startMs: 16380, endMs: 16560 },
  { text: 'don\'t.', startMs: 16560, endMs: 16860 },
  { text: 'We', startMs: 16860, endMs: 17420 },
  { text: 'map', startMs: 17420, endMs: 17680 },
  { text: 'your', startMs: 17680, endMs: 17900 },
  { text: 'entire', startMs: 17900, endMs: 18160 },
  { text: 'market.', startMs: 18160, endMs: 18700 },
  { text: 'Who', startMs: 18700, endMs: 19260 },
  { text: 'ranks', startMs: 19260, endMs: 19560 },
  { text: 'above', startMs: 19560, endMs: 19800 },
  { text: 'you.', startMs: 19800, endMs: 20160 },
  { text: 'What\'s', startMs: 20160, endMs: 20480 },
  { text: 'working.', startMs: 20480, endMs: 20940 },
  { text: 'What\'s', startMs: 20940, endMs: 21260 },
  { text: 'not.', startMs: 21260, endMs: 21700 },
  { text: 'Free', startMs: 21700, endMs: 23040 },
  { text: 'Market', startMs: 23040, endMs: 23340 },
  { text: 'Report.', startMs: 23340, endMs: 23900 },
  { text: 'Grab', startMs: 23900, endMs: 24360 },
  { text: 'yours', startMs: 24360, endMs: 24620 },
  { text: 'now.', startMs: 24620, endMs: 25140 },
];

// Scene definitions with word groupings (aligned to Whisper timing)
export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Hook',
    startMs: 0,
    endMs: 1520,
    words: WORD_TIMINGS.slice(0, 3),
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Contrast',
    startMs: 1520,
    endMs: 3540,
    words: WORD_TIMINGS.slice(3, 7),
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Setup',
    startMs: 3540,
    endMs: 7620,
    words: WORD_TIMINGS.slice(7, 13),
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 4,
    name: 'Pain',
    startMs: 7620,
    endMs: 13100,
    words: WORD_TIMINGS.slice(13, 24),
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomInLower,
  },
  {
    id: 5,
    name: 'Question',
    startMs: 13100,
    endMs: 16860,
    words: WORD_TIMINGS.slice(24, 32),
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 6,
    name: 'Solution',
    startMs: 16860,
    endMs: 21700,
    words: WORD_TIMINGS.slice(32, 45),
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 7,
    name: 'CTA',
    startMs: 21700,
    endMs: 25140,
    words: WORD_TIMINGS.slice(45),
    ...SCENE_COLORS.scene7,
    backgroundImage: SCENE_IMAGES.scene7,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

// Logo scene (after voiceover) - 4 seconds for full logo playback
export const LOGO_SCENE = {
  startMs: 25140,
  endMs: 29140,
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
